import { type PaletteKey, type Pixel, type PixelCanvas, sphere, sprite } from '../../lib';
import { field, lobe, strand } from './fx.sprite';

/**
 * Effect skins' shots (ADR-0113) at 4× density: each skin's bullet, muzzle flash and hit burst. A bullet keeps the stock
 * pellet's size on screen (about 4×4 world px of body) and a bright core, so it reads and dodges as one; the client
 * draws the pellet's glow (`skin_glow`, tinted) and tracer under it in the skin's colour. Flashes point right from the
 * muzzle (origin at the left middle, as `muzzle_flash`) and are as big and bright as it on their first frame; bursts are
 * centred on where the shot ended, as big as `impact`. Outline-free, bright: they are effects.
 */

type Pt = readonly [number, number];
type Spot = readonly [x: number, y: number, r: number];

/** The angle of (x, y) round (cx, cy), 0..1 from the right, clockwise. */
const turn = (x: number, y: number, cx: number, cy: number): number =>
  (Math.atan2(y - cy, x - cx) / (Math.PI * 2) + 1) % 1;

/**
 * An eight-point burst at (cx, cy) by heat (1 in the middle, 0 at its edge): long rays `long` px out along the axes,
 * `short` on the diagonals, a round core `core` px across, each pixel toned by `tone(heat)`.
 */
function burst8(
  c: PixelCanvas,
  cx: number,
  cy: number,
  long: number,
  short: number,
  core: number,
  tone: (h: number) => Pixel,
) {
  return field(c, (x, y) => {
    const [dx, dy] = [x - cx, y - cy];
    const a = Math.atan2(dy, dx);
    const reach =
      core + (long - core) * Math.abs(Math.cos(2 * a)) ** 14 + (short - core) * Math.abs(Math.sin(2 * a)) ** 14;
    const h = 1 - Math.hypot(dx, dy) / reach;
    return h > 0 ? tone(h) : null;
  });
}

/** A twinkle: a white point with arms `arm` px long in `col` (a plus: it reads as a sparkle at any size). */
function twinkle(c: PixelCanvas, x: number, y: number, arm: number, col: PaletteKey = 'y'): void {
  for (let i = 1; i <= arm; i++) {
    const tip = i === arm && arm > 1 ? (col === 'y' ? 'Y' : col) : col;
    c.set(x + i, y, tip)
      .set(x - i, y, tip)
      .set(x, y + i, tip)
      .set(x, y - i, tip);
  }
  c.set(x, y, 'w');
}

// --- Bubble gun -----------------------------------------------------------------------------------------------------

/** A soap film's colours round its rim, as it turns: the iridescence. */
const SHEEN: readonly PaletteKey[] = ['c', 'w', 'i', 'p', 'y', 'c', 'b', 'c'];

/**
 * A soap bubble at (cx, cy), radii rx, ry: a 1-px edge (blue on its shade side, so it parts from the grass; cyan where
 * lit), inside it a 2-px rim of sheen turning by `spin` (0..1), a white glint arcing round its top-left and a cyan
 * crescent of reflection bottom-right; clear inside. A small bubble (`r` under 4) keeps the edge and a 1-px sheen.
 */
function bubble(c: PixelCanvas, cx: number, cy: number, rx: number, ry: number, spin: number): PixelCanvas {
  const rim = Math.min(rx, ry) < 4 ? 2 : 3;
  return field(c, (x, y) => {
    const nx = (x - cx) / rx;
    const ny = (y - cy) / ry;
    const d = Math.hypot(nx, ny);
    if (d > 1) return null;
    const band = (1 - d) * Math.min(rx, ry);
    const a = turn(x, y, cx, cy);
    if (band < 1) return nx + ny > -0.2 ? 'B' : 'c';
    if (band < rim) return SHEEN[Math.floor((a + spin) * SHEEN.length) % SHEEN.length] as PaletteKey;
    // The glint: a white arc high on the lit side; the reflection low on the far side.
    if (band < rim + 1.3 && a > 0.56 && a < 0.74) return 'w';
    if (band < rim + 0.9 && a > 0.06 && a < 0.2) return 'c';
    return null;
  });
}

const bubbleShot = sprite({
  name: 'bubble_shot',
  group: 'fx',
  size: [20, 20],
  origin: [10, 10],
  density: 4,
  notes: `Bubble gun's bullet (ADR-0113): a soap bubble 16 art px across (the stock pellet's size), a 1-px edge (blue on its
shade side, cyan lit), a 2-px rim of sheen (cyan, white, pink, magenta, yellow) turning frame by frame, a white glint
arcing round its top-left and a cyan reflection; clear inside (the glow under it lights it). Drawn upright (its glint
stays top-left), wobbling. Rubric (4×): S4 R4 P5 L4 C4 A4 F4. Weakest: its sheen bands are 2-3 px, a colourful ring more
than a film at game zoom.`,
  anims: {
    idle: {
      fps: 12,
      frames: [
        (c: PixelCanvas) => bubble(c, 10, 10, 8.2, 7.6, 0),
        (c: PixelCanvas) => bubble(c, 10, 10, 7.9, 7.9, 0.12),
        (c: PixelCanvas) => bubble(c, 10, 10, 7.6, 8.2, 0.25),
        (c: PixelCanvas) => bubble(c, 10, 10, 7.9, 7.9, 0.37),
      ],
    },
  },
});

/** Soap foam: white balls shaded cyan and blue underneath, `spots` [x, y, r]. */
function foam(c: PixelCanvas, spots: readonly Spot[]): void {
  for (const [x, y, r] of spots) sphere(c, x, y, r, r * 0.9, ['b', 'c', '5', 'w'], { bands: [-0.25, 0.15, 0.55] });
}

const bubbleMuzzle = sprite({
  name: 'bubble_muzzle',
  group: 'fx',
  size: [48, 44],
  origin: [4, 20],
  density: 4,
  notes: `Bubble gun's muzzle flash (ADR-0113): a puff of soap foam out of the wand (white balls shaded cyan and blue), a
froth of bubbles blown out of it, flying on and shrinking over 3 frames while the foam breaks up. Points right (origin
left-middle). Rubric (4×): S4 R4 P5 L4 C4 A4 F4. Weakest: the foam's balls read as a cloud more than as lather.`,
  anims: {
    flash: {
      fps: 30,
      loop: false,
      frames: [
        (c: PixelCanvas) => {
          foam(c, [
            [7, 20.5, 5],
            [13, 17, 4.6],
            [13, 24.5, 4.2],
            [19, 20.5, 4.4],
          ]);
          bubble(c, 27, 14, 4.6, 4.6, 0.1);
          bubble(c, 28, 27.5, 4, 4, 0.5);
          bubble(c, 34, 20.5, 3.2, 3.2, 0.3);
        },
        (c: PixelCanvas) => {
          foam(c, [
            [10, 18.5, 3.2],
            [12, 23.5, 2.8],
            [16, 20.5, 3],
          ]);
          bubble(c, 26, 12, 5, 5, 0.25);
          bubble(c, 32, 29, 4.4, 4.4, 0.65);
          bubble(c, 39, 19.5, 3.6, 3.6, 0.45);
          bubble(c, 22, 31, 2.6, 2.6, 0.8);
        },
        (c: PixelCanvas) => {
          foam(c, [[15, 21, 1.8]]);
          bubble(c, 30, 10, 4.2, 4.2, 0.4);
          bubble(c, 35, 31, 3.6, 3.6, 0.8);
          bubble(c, 43, 18.5, 3, 3, 0.6);
          bubble(c, 22, 33, 2.2, 2.2, 0.1);
        },
      ],
    },
  },
});

/** A pop at (22, 22): `f` 0, a bright ring snapping out round a white flash; 1, its rim in arcs; 2, droplets. */
function pop(f: number) {
  return (c: PixelCanvas) => {
    const [cx, cy] = [22, 22];
    const r = [9, 13, 17][f] as number;
    const width = [2.6, 1.8, 0][f] as number;
    if (f === 0) burst8(c, cx, cy, 8, 4, 3, (h) => (h > 0.4 ? 'w' : 'c'));
    if (width > 0)
      field(c, (x, y) => {
        const d = Math.hypot(x - cx, y - cy);
        if (Math.abs(d - r) > width / 2) return null;
        const a = turn(x, y, cx, cy);
        // Whole on the first frame; then broken into arcs with gaps.
        if (f > 0 && (a * 5 + 0.1) % 1 > 0.55) return null;
        return SHEEN[Math.floor((a + 0.05) * SHEEN.length) % SHEEN.length] as PaletteKey;
      });
    // Short pop lines past the ring, then droplets flying on.
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
      const [ux, uy] = [Math.cos(a), Math.sin(a)];
      if (f === 0)
        strand(
          c,
          [
            [cx + ux * (r + 3), cy + uy * (r + 3)],
            [cx + ux * (r + 7), cy + uy * (r + 7)],
          ],
          1.4,
          () => 'w',
        );
      else {
        const rr = r + (f === 1 ? 4 : 1);
        const [x, y] = [Math.round(cx + ux * rr), Math.round(cy + uy * rr + (f === 2 ? 2 : 0))];
        c.set(x, y, 'w').set(x + 1, y, 'c');
        if (f === 1) c.set(x, y + 1, 'c').set(x + 1, y + 1, 'b');
      }
    }
  };
}

const bubblePop = sprite({
  name: 'bubble_pop',
  group: 'fx',
  size: [44, 44],
  origin: [22, 22],
  density: 4,
  notes: `Bubble gun's hit (ADR-0113): the bubble pops, a ring of sheen snapping out round a white flash with pop lines past
it, then its rim in arcs flung out and droplets flying on. 3 frames, outline-free. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.
Weakest: the last frame is only droplets, gone in a blink.`,
  anims: { burst: { fps: 20, loop: false, frames: [pop(0), pop(1), pop(2)] } },
});

// --- Angel wings ----------------------------------------------------------------------------------------------------

/**
 * A halo at (cx, cy): a gold ring seen from above at an angle (an ellipse rx × ry, `t` px thick): its far half the ring's
 * inside (its inner edge a step darker), its near half lit with a burnt rim under it, a white glint `at` (0..1) round it.
 */
function halo(c: PixelCanvas, cx: number, cy: number, rx: number, ry: number, t: number, at: number): PixelCanvas {
  return field(c, (x, y) => {
    const o = Math.hypot((x - cx) / rx, (y - cy) / ry);
    const i = Math.hypot((x - cx) / (rx - t), (y - cy) / Math.max(0.5, ry - t * 0.6));
    if (o > 1 || i < 1) return null;
    const a = turn(x, y, cx, cy);
    const glint = Math.min(Math.abs(a - at), 1 - Math.abs(a - at));
    if (glint < 0.06) return 'w';
    if (y < cy) return i < 1.18 || o > 0.9 ? 'Y' : 'y';
    return o > 0.86 ? 'U' : glint < 0.12 || x < cx + rx * 0.3 ? 'y' : 'Y';
  });
}

const haloShot = sprite({
  name: 'halo_shot',
  group: 'fx',
  size: [24, 16],
  origin: [12, 8],
  density: 4,
  notes: `Angel wings' bullet (ADR-0113): a spinning golden halo, a ring 19×10 art px seen from above at an angle (yellow gold,
its far half the ring's inside, a burnt rim under its near half to part it from the grass), a white glint running round
it as it spins; drawn upright. Rubric (4×): S4 R4 P5 L4 C4 A4 F4. Weakest: at game zoom its hole is 3 px, a gold O.`,
  anims: {
    idle: {
      fps: 16,
      frames: [0, 0.25, 0.5, 0.75].map((at) => (c: PixelCanvas) => halo(c, 12, 8, 9.6, 5, 3.1, at)),
    },
  },
});

/** Holy light by heat: a white core, yellow, gold, a burnt rim. */
const holy = (h: number): Pixel => (h > 0.55 ? 'w' : h > 0.3 ? 'y' : h > 0.12 ? 'Y' : 'U');

const angelMuzzle = sprite({
  name: 'angel_muzzle',
  group: 'fx',
  size: [48, 44],
  origin: [4, 20],
  density: 4,
  notes: `Angel wings' muzzle flash (ADR-0113): a burst of holy light at the trumpet's bell, an eight-point star (white core,
yellow, gold, a burnt rim) longest along the shot, twinkles flying on ahead; it shrinks to the twinkles over 3 frames.
Points right (origin left-middle). Rubric (4×): S5 R4 P5 L4 C4 A4 F4. Weakest: the star is symmetric, little sense of
the shot's way but its forward twinkles.`,
  anims: {
    flash: {
      fps: 30,
      loop: false,
      frames: [
        (c: PixelCanvas) => {
          burst8(c, 15, 20.5, 14, 7, 4.5, holy);
          twinkle(c, 33, 14, 3);
          twinkle(c, 34, 27, 2);
        },
        (c: PixelCanvas) => {
          burst8(c, 17, 20.5, 9, 5, 3, holy);
          twinkle(c, 37, 12, 3);
          twinkle(c, 39, 28, 2);
          twinkle(c, 43, 20, 2);
        },
        (c: PixelCanvas) => {
          twinkle(c, 41, 11, 2);
          twinkle(c, 43, 29, 1);
          twinkle(c, 45, 20, 1);
          twinkle(c, 20, 20, 2);
        },
      ],
    },
  },
});

const angelSparkle = sprite({
  name: 'angel_sparkle',
  group: 'fx',
  size: [44, 44],
  origin: [22, 22],
  density: 4,
  notes: `Angel wings' hit (ADR-0113): a sparkle, an eight-point star of holy light (white core, yellow, gold, a burnt rim),
then a smaller one in a ring of twinkles, the twinkles flying out as it fades. 4 frames, outline-free. Rubric (4×): S5 R4
P5 L4 C4 A4 F4. Weakest: the twinkles are pluses, a touch mechanical at 8×.`,
  anims: {
    burst: {
      fps: 20,
      loop: false,
      frames: [
        (c: PixelCanvas) => burst8(c, 22, 22, 15, 8, 4.5, holy),
        (c: PixelCanvas) => {
          burst8(c, 22, 22, 9, 5, 3, holy);
          for (const [x, y] of [
            [10, 11],
            [34, 12],
            [33, 33],
            [11, 32],
          ] as const)
            twinkle(c, x, y, 2);
        },
        (c: PixelCanvas) => {
          twinkle(c, 22, 22, 3);
          for (const [x, y] of [
            [7, 8],
            [37, 9],
            [36, 36],
            [8, 35],
          ] as const)
            twinkle(c, x, y, 2);
        },
        (c: PixelCanvas) => {
          for (const [x, y] of [
            [5, 6],
            [39, 7],
            [38, 38],
          ] as const)
            twinkle(c, x, y, 1);
        },
      ],
    },
  },
});

// --- Plasma gun -----------------------------------------------------------------------------------------------------

/** Plasma by heat: a white core, cyan, blue, a violet fringe. */
const plasmaTone = (h: number): Pixel => (h <= 0 ? null : h > 0.62 ? 'w' : h > 0.36 ? 'c' : h > 0.16 ? 'b' : 'P');

/** A zigzag arc of electricity from a to b over `n` kinks, `amp` px off its line either way; 1 px, cyan with white. */
function arc(c: PixelCanvas, a: Pt, b: Pt, n: number, amp: number, seed: number): void {
  let prev = a;
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const off = i === n ? 0 : (((seed * 7 + i * 13) % 5) / 2 - 1) * amp;
    const nx = -(b[1] - a[1]);
    const ny = b[0] - a[0];
    const len = Math.hypot(nx, ny) || 1;
    const p: Pt = [a[0] + (b[0] - a[0]) * t + (nx / len) * off, a[1] + (b[1] - a[1]) * t + (ny / len) * off];
    c.line(prev[0], prev[1], p[0], p[1], i % 2 ? 'c' : 'w');
    prev = p;
  }
}

function bolt(f: number) {
  return (c: PixelCanvas) => {
    field(c, (x, y) =>
      plasmaTone(Math.max(lobe(x, y, [3, 8, 1], [19, 8, 4.2]) * 0.75, lobe(x, y, [19, 8, 4.6], [26, 8, 4]))),
    );
    arc(c, [8, 6], [18, 4], 3, 1.5, f);
    arc(c, [10, 10], [20, 12.5], 3, 1.5, f + 2);
  };
}

const plasmaShot = sprite({
  name: 'plasma_shot',
  group: 'fx',
  size: [32, 16],
  origin: [21, 8],
  density: 4,
  notes: `Plasma gun's bullet (ADR-0113): a bolt of plasma, a white-hot head 9 art px tall (the stock seed's size) in cyan, blue
and a violet fringe, its tail tapering back, a crackle of arcs flickering along it. Head right, turned along its flight.
Rubric (4×): S5 R5 P5 L4 C4 A4 F4. Weakest: the violet fringe is 1 px, lost at game zoom.`,
  anims: { idle: { fps: 15, frames: [bolt(0), bolt(1), bolt(2)] } },
});

function zap(f: number) {
  return (c: PixelCanvas) => {
    const k = [1, 0.65, 0.35][f] as number;
    field(c, (x, y) => plasmaTone(lobe(x, y, [3, 20.5, 3.5 * k], [16, 20.5, 9 * k]) * (f === 2 ? 0.55 : 1)));
    for (let i = 0; i < 3; i++) {
      const a = -0.75 + i * 0.75 + f * 0.25;
      const r = 14 + f * 8;
      arc(c, [10 + f * 4, 20.5], [10 + Math.cos(a) * r, 20.5 + Math.sin(a) * r], 3, 2, i + f);
    }
  };
}

const plasmaMuzzle = sprite({
  name: 'plasma_muzzle',
  group: 'fx',
  size: [48, 44],
  origin: [4, 20],
  density: 4,
  notes: `Plasma gun's muzzle flash (ADR-0113): a cyan burst with a white core between the prongs, arcs of electricity forking
out ahead; smaller and broken over 3 frames. Points right (origin left-middle). Rubric (4×): S4 R5 P5 L4 C4 A4 F4.
Weakest: the arcs are 1 px, thin next to the stock flame.`,
  anims: { flash: { fps: 30, loop: false, frames: [zap(0), zap(1), zap(2)] } },
});

function crackle(f: number) {
  return (c: PixelCanvas) => {
    const k = [1, 0.6, 0.3, 0][f] as number;
    if (k > 0) field(c, (x, y) => plasmaTone(1 - Math.hypot(x - 22, y - 22) / (10 * k)));
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2 + f * 0.6;
      const r0 = 4 + f * 4;
      const r1 = 13 + f * 4;
      if (f === 3 && i % 2) continue;
      arc(
        c,
        [22 + Math.cos(a) * r0, 22 + Math.sin(a) * r0],
        [22 + Math.cos(a) * r1, 22 + Math.sin(a) * r1],
        3,
        2,
        i + f,
      );
    }
  };
}

const plasmaCrackle = sprite({
  name: 'plasma_crackle',
  group: 'fx',
  size: [44, 44],
  origin: [22, 22],
  density: 4,
  notes: `Plasma gun's hit (ADR-0113): a crackle, a white-hot flash in cyan with a violet fringe, arcs of electricity forking
out of it and breaking up. 4 frames, outline-free. Rubric (4×): S4 R5 P5 L4 C4 A4 F4. Weakest: the arcs repeat a pattern.`,
  anims: { burst: { fps: 20, loop: false, frames: [crackle(0), crackle(1), crackle(2), crackle(3)] } },
});

// --- Garden hose ----------------------------------------------------------------------------------------------------

/**
 * Water lit from above: `h` how far inside (0 its edge … 1 its middle), (y − cy) / r where it is from top to bottom: lit
 * cyan on top, blue, deep blue underneath and round its lower edge, a white glint high in its middle.
 */
const waterTone = (h: number, y: number, cy: number, r = 3): Pixel => {
  if (h <= 0) return null;
  const t = (y - cy) / r;
  if (h > 0.62 && t < -0.15) return 'w';
  if (h < 0.22) return t < -0.2 ? 'b' : 'B';
  return t < -0.3 ? 'c' : t > 0.35 ? 'B' : 'b';
};

/** Drops of water at `spots` [x, y, r]. */
function drops(c: PixelCanvas, spots: readonly Spot[]): void {
  for (const [x, y, r] of spots) field(c, (px, py) => waterTone(1 - Math.hypot(px - x, py - y) / r, py, y, r));
}

function jet(f: number) {
  return (c: PixelCanvas) => {
    // The head: a fat drop; the jet behind it thinner, then broken into drops that shift each frame.
    field(c, (x, y) => waterTone(lobe(x, y, [19, 8, 3.6], [24.5, 8, 4.4]), y, 8, 4.4));
    field(c, (x, y) =>
      c.get(Math.floor(x), Math.floor(y)) ? null : waterTone(lobe(x, y, [7, 8, 1.2], [18, 8, 2.8]) * 0.55, y, 8, 2.8),
    );
    for (const [x, y] of f
      ? [
          [4, 7],
          [9, 10],
          [13, 5],
        ]
      : [
          [2, 9],
          [7, 6],
          [12, 10],
        ])
      c.set(x as number, y as number, 'c').set((x as number) + 1, y as number, 'b');
  };
}

const waterShot = sprite({
  name: 'water_shot',
  group: 'fx',
  size: [32, 16],
  origin: [22, 8],
  density: 4,
  notes: `Garden hose's bullet (ADR-0113): a short jet of water, a fat drop at its head (lit cyan on top, blue, deep blue
underneath, a white glint: the stock seed's size) and the jet tapering behind it, breaking into drops. Head right, turned
along its flight. Rubric (4×): S4 R4 P5 L5 C4 A4 F4. Weakest: deep blue on the grass's shade is low in contrast; its glow
and tracer carry it there.`,
  anims: { idle: { fps: 15, frames: [jet(0), jet(1)] } },
});

/** A spray out of the nozzle: streaks fanning out (`fan` [angle, from, to] in px), white at their roots. */
function spray(c: PixelCanvas, fan: readonly (readonly [number, number, number])[], width: number): void {
  for (const [a, r0, r1] of fan)
    strand(
      c,
      [
        [4 + Math.cos(a) * r0, 20.5 + Math.sin(a) * r0],
        [4 + Math.cos(a) * r1, 20.5 + Math.sin(a) * r1],
      ],
      width,
      (along, side) => (along < 0.3 ? 'w' : side > 0.3 ? 'c' : side < -0.4 ? 'B' : 'b'),
      2,
    );
}

const hoseMuzzle = sprite({
  name: 'hose_muzzle',
  group: 'fx',
  size: [48, 44],
  origin: [4, 20],
  density: 4,
  notes: `Garden hose's muzzle flash (ADR-0113): a spray, a white burst of mist at the nozzle and streaks of water fanning out
of it (white at their roots, cyan lit, blue, deep blue underneath), breaking into drops as they fly on over 3 frames.
Points right (origin left-middle). Rubric (4×): S4 R4 P5 L4 C4 A4 F4. Weakest: the streaks are straight; a real spray
droops.`,
  anims: {
    flash: {
      fps: 30,
      loop: false,
      frames: [
        (c: PixelCanvas) => {
          spray(
            c,
            [
              [-0.42, 4, 18],
              [-0.14, 4, 26],
              [0.14, 4, 25],
              [0.42, 4, 17],
            ],
            3.4,
          );
          burst8(c, 8, 20.5, 8, 5, 4, (h) => (h > 0.45 ? 'w' : 'c'));
        },
        (c: PixelCanvas) => {
          spray(
            c,
            [
              [-0.42, 12, 22],
              [-0.14, 14, 31],
              [0.14, 13, 30],
              [0.42, 12, 21],
            ],
            2.6,
          );
          drops(c, [
            [27, 10, 2],
            [36, 18, 2.2],
            [35, 25, 2],
            [26, 31, 1.8],
          ]);
        },
        (c: PixelCanvas) =>
          drops(c, [
            [30, 8, 1.8],
            [41, 17, 2],
            [40, 26, 1.8],
            [29, 33, 1.6],
            [22, 21, 1.6],
          ]),
      ],
    },
  },
});

/** The splash's crown drops per frame: [x, y, r], flying out and up from where it hit, then falling. */
const CROWN: readonly (readonly Spot[])[] = [
  [],
  [
    [10, 22, 2.6],
    [13, 13, 2.6],
    [22, 9, 2.8],
    [31, 13, 2.6],
    [34, 22, 2.6],
    [17, 30, 2.2],
    [27, 30, 2.2],
  ],
  [
    [5, 25, 2.2],
    [9, 12, 2.2],
    [22, 5, 2.4],
    [35, 12, 2.2],
    [39, 25, 2.2],
    [14, 35, 1.8],
    [30, 35, 1.8],
  ],
  [
    [3, 31, 1.6],
    [7, 18, 1.6],
    [22, 8, 1.8],
    [37, 18, 1.6],
    [41, 31, 1.6],
  ],
];

const waterSplash = sprite({
  name: 'water_splash',
  group: 'fx',
  size: [44, 44],
  origin: [22, 22],
  density: 4,
  notes: `Garden hose's hit (ADR-0113): a splash, a burst of water (white core, cyan, blue spikes) breaking into a crown of
drops that fly out and up, then fall. 4 frames, outline-free. Rubric (4×): S4 R4 P5 L4 C4 A4 F4. Weakest: the crown is
a ring of round drops; a real splash tears into streaks.`,
  anims: {
    burst: {
      fps: 20,
      loop: false,
      frames: CROWN.map((spots, f) => (c: PixelCanvas) => {
        if (f === 0) burst8(c, 22, 22, 12, 10, 7.5, (h) => (h > 0.5 ? 'w' : h > 0.25 ? 'c' : 'b'));
        if (f === 1) burst8(c, 22, 22, 6, 4, 3.5, (h) => (h > 0.5 ? 'c' : 'b'));
        drops(c, spots);
      }),
    },
  },
});

const skinGlow = sprite({
  name: 'skin_glow',
  group: 'fx',
  size: [36, 36],
  origin: [16, 16],
  density: 4,
  notes: `The glow under an effect skin's bullet and at its muzzle (ADR-0113): \`glow\`'s rings in greys (5, 4, 3, 2), about
its fire ramp's brightness, instead of the fire ramp, so the client's tint gives it the skin's colour (a cool tint on
the warm glow went green). Additive, scaled and faded by the client. Rubric (4×): S4 R5 P5 L5 C5 A- F5.`,
  anims: {
    idle: {
      frames: [
        (c: PixelCanvas) =>
          field(c, (x, y) => {
            const r = Math.hypot(x - 18, y - 18);
            return r < 6.6 ? '5' : r < 11 ? '4' : r < 15 ? '3' : r < 17.8 ? '2' : null;
          }),
      ],
    },
  },
});

export default [
  skinGlow,
  bubbleShot,
  bubbleMuzzle,
  bubblePop,
  haloShot,
  angelMuzzle,
  angelSparkle,
  plasmaShot,
  plasmaMuzzle,
  plasmaCrackle,
  waterShot,
  hoseMuzzle,
  waterSplash,
];
